import React, { FC, FunctionComponent, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Accordion,
  List,
  ListItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ViewChangesDatagrid } from "./ViewChangesDatagrid";
import { UserChip } from "../UserChip";
import { ChangeInformationCard } from "./ChangeInformationCard";
import { formatDateHyphen } from "../../utils/time";
import {
  Log,
  CallDetailHistory,
  RefundHistory,
  ViewChangesDialogProps,
  HistoryInformation,
} from "./types";
import { getAxios, route } from "../../utils/endpoints/";

export const ViewChangesDialog = <T extends object>({
  id,
  entity,
  label,
  isOpen,
  onClose,
}: ViewChangesDialogProps) => {
  let endpoint: string = "";
  const { lead, warranty, refund } = route.apiHistory(id);

  const [author, setAuthor] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [logs, setLogs] = useState<Log[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [data, setData] = useState<any[]>([]);

  switch (entity) {
    case "LEAD": {
      endpoint = lead;
      break;
    }

    case "REFUND": {
      endpoint = refund;
      break;
    }

    case "WARRANTY": {
      endpoint = warranty;
      break;
    }
  }

  const convertLogToRows = (
    log: T
  ): { new: string; old: string; fieldName: string }[] => {
    let rows: { new: string; old: string; fieldName: string }[] = [];

    Object.keys(log).forEach((key) => {
      let row: any | null = null;

      if (key.startsWith("old")) {
        const newKey = key.replace("old", "new");

        row = {
          old: log[key as keyof typeof log] as unknown as string,
          new: log[newKey as keyof typeof log] as unknown as string,
          fieldName: key.replace(/^old|^new/, ""),
        };

        if (row.old !== row.new) {
          rows.push(row);
        }
      }
    });

    return rows!;
  };

  const convertDataToLogs = (data: HistoryInformation[]): Log[] => {
    const logs: Log[] = [];

    data.forEach((item) => {
      logs.push({ id: item.logId, text: item.updatedOn });
    });

    return logs;
  };

  const selectLog = (id: number) => {
    const log = data.find((item, index) => {
      return item.logId === id;
    });

    setAuthor(log.updatedBy);
    setDate(formatDateHyphen(new Date(log.updatedOn)));

    setRows(convertLogToRows(log));
  };

  const onFetchChanges = async () => {
    try {
      const response = await getAxios(
        endpoint,
        "GET",
        undefined,
        entity === "LEAD" ? { id } : undefined
      );
      const responseData = await response.data;
      const data: (T & HistoryInformation)[] = Array.isArray(responseData)
        ? responseData
        : [responseData];

      setAuthor(data[0].updatedBy);
      setDate(formatDateHyphen(new Date(data[0].updatedOn)));

      setLogs(convertDataToLogs(data));
      setRows(convertLogToRows(data[0] as T));

      setData(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    onFetchChanges();
  }, []);

  return (
    <Dialog open={isOpen} fullWidth maxWidth="lg">
      <DialogTitle>
        <IconButton
          onClick={onClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex">
          <Box width={600}>
            <ViewChangesDatagrid rows={rows} />
          </Box>
          <Box m={2}>
            <ChangeInformationCard
              onSelectLog={(id) => selectLog(id)}
              label={label}
              author={author}
              date={date}
              logs={logs}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
